import Fields from 'defs/ga/fields/anamnesis';
import GlobalFields from 'defs/ga/fields/global';
import { getVirtualPageTitle, getVirtualPageUrl } from 'helper/ga';
import { GenderKeyType } from 'types/GenderKey';
import { AllergyType, NutritionPhilosophyType } from '../../../types/DietaryHabits';

export const BASE_TRACKING_DATA = {
	[GlobalFields.Event]: 'anmeldung-formular',
	[Fields.FormType]: 'coach-anamnese',
	[GlobalFields.VirtualPageTitle]: (() => getVirtualPageTitle())(),
	[GlobalFields.VirtualPageURL]: (() => getVirtualPageUrl())(),
};

export const trackSelectCoach = ({ coach }: { coach: Object }): Object => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step1',
	[Fields.Coach]: coach,
});

export const trackSelectGender = ({ coach, gender }: { coach: Object; gender: GenderKeyType }): Object => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step2',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
});

export const trackDetermineAge = ({
	coach,
	gender,
	age,
	yearofBirth,
}: {
	coach: any;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step3',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
});

export const trackDetermineHeight = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step4',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
});

export const trackDetermineWeight = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step5',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
});

export const trackDefineHealthTarget = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
	activityGoal,
	activityWeight,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
	activityGoal: string;
	activityWeight: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step6',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
	[Fields.ActivityGoal]: activityGoal,
	[Fields.WeightReduceGoal]: activityWeight,
});

export const trackAcceptHealthTarget = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
	activityGoal,
	activityWeight,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
	activityGoal: string;
	activityWeight: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step6-accept',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
	[Fields.ActivityGoal]: activityGoal,
	[Fields.WeightReduceGoal]: activityWeight,
});

export const trackDietaryHabits = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
	activityGoal,
	activityWeight,
	nutritionPhilosophy,
	allergies,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
	activityGoal: string;
	activityWeight: number;
	nutritionPhilosophy: NutritionPhilosophyType;
	allergies: AllergyType[];
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step7',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
	[Fields.ActivityGoal]: activityGoal,
	[Fields.WeightReduceGoal]: activityWeight,
	[Fields.NutritionPhilosophy]: nutritionPhilosophy,
	[Fields.Allergies]: allergies,
});

export const trackActivityLevel = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
	activityGoal,
	activityWeight,
	nutritionPhilosophy,
	allergies,
	activityLevel,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
	activityGoal: string;
	activityWeight: number;
	nutritionPhilosophy: NutritionPhilosophyType;
	allergies: AllergyType[];
	activityLevel: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step8',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
	[Fields.ActivityGoal]: activityGoal,
	[Fields.WeightReduceGoal]: activityWeight,
	[Fields.NutritionPhilosophy]: nutritionPhilosophy,
	[Fields.Allergies]: allergies,
	[Fields.ActivityLevel]: activityLevel,
});

export const trackCheckData = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
	activityGoal,
	activityWeight,
	nutritionPhilosophy,
	allergies,
	activityLevel,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
	activityGoal: string;
	activityWeight: number;
	nutritionPhilosophy: NutritionPhilosophyType;
	allergies: AllergyType[];
	activityLevel: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'step9',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
	[Fields.ActivityGoal]: activityGoal,
	[Fields.WeightReduceGoal]: activityWeight,
	[Fields.NutritionPhilosophy]: nutritionPhilosophy,
	[Fields.Allergies]: allergies,
	[Fields.ActivityLevel]: activityLevel,
});

export const trackDsgvo = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
	activityGoal,
	activityWeight,
	nutritionPhilosophy,
	allergies,
	activityLevel,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
	activityGoal: string;
	activityWeight: number;
	nutritionPhilosophy: NutritionPhilosophyType;
	allergies: AllergyType[];
	activityLevel: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'dsgvo',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
	[Fields.ActivityGoal]: activityGoal,
	[Fields.WeightReduceGoal]: activityWeight,
	[Fields.NutritionPhilosophy]: nutritionPhilosophy,
	[Fields.Allergies]: allergies,
	[Fields.ActivityLevel]: activityLevel,
});

export const trackDisclaimer = ({
	coach,
	gender,
	age,
	yearofBirth,
	height,
	weight,
	activityGoal,
	activityWeight,
	nutritionPhilosophy,
	allergies,
	activityLevel,
}: {
	coach: Object;
	gender: GenderKeyType;
	age: number;
	yearofBirth: number;
	height: number;
	weight: number;
	activityGoal: string;
	activityWeight: number;
	nutritionPhilosophy: NutritionPhilosophyType;
	allergies: AllergyType[];
	activityLevel: number;
}) => ({
	...BASE_TRACKING_DATA,
	[Fields.FormStep]: 'success',
	[Fields.Coach]: coach,
	[Fields.Gender]: gender,
	[Fields.Age]: age,
	[Fields.BirthYear]: yearofBirth,
	[Fields.Height]: height,
	[Fields.Weight]: weight,
	[Fields.ActivityGoal]: activityGoal,
	[Fields.WeightReduceGoal]: activityWeight,
	[Fields.NutritionPhilosophy]: nutritionPhilosophy,
	[Fields.Allergies]: allergies,
	[Fields.ActivityLevel]: activityLevel,
});
